import React, { useCallback, useEffect, useState } from 'react';
import {
  Alert, AlertIcon, Badge, Box, Button, Code, HStack, Input, Modal,
  ModalBody, ModalCloseButton, ModalContent, ModalFooter, ModalHeader,
  ModalOverlay, Select, Text, VStack, useToast, FormControl, FormLabel,
  FormHelperText, List, ListItem, Divider,
} from '@chakra-ui/react';

import { Trans, useTranslation } from 'react-i18next';

import channexService, { defaultCallbackBaseUrl } from '../../services/channexService';

/**
 * Put a hotel onto Channex in one action.
 *
 * Runs config -> property -> catalogue -> webhook server-side. No API key is
 * collected here: the backend clones the encrypted credential from an
 * existing Channex config, because a key encrypted outside the target
 * environment cannot be decrypted by it.
 */
const AddHotelModal = ({ isOpen, onClose, onDone }) => {
  const [hotels, setHotels] = useState([]);
  const [loading, setLoading] = useState(false);
  const [hotelId, setHotelId] = useState('');
  // Default to the API this admin build talks to — the webhook receiver
  // lives on that same backend. An empty default silently skipped webhook
  // registration on prod (register_webhook keys off this value).
  const [callbackUrl, setCallbackUrl] = useState(
    defaultCallbackBaseUrl()
  );
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);
  const toast = useToast();
  const { t } = useTranslation();

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const data = await channexService.onboardableHotels();
      setHotels(data.hotels || []);
    } catch (err) {
      toast({ title: t('channexAddHotel.toasts.loadHotelsFailed'), description: err.message, status: 'error' });
    } finally {
      setLoading(false);
    }
  }, [toast, t]);

  useEffect(() => { if (isOpen) { load(); setResult(null); } }, [isOpen, load]);

  const selected = hotels.find((h) => String(h.id) === String(hotelId));

  const run = async (dryRun) => {
    setBusy(true);
    setResult(null);
    try {
      const data = await channexService.onboard({
        hotel_id: Number(hotelId),
        callback_base_url: callbackUrl || undefined,
        register_webhook: Boolean(callbackUrl),
        dry_run: dryRun,
      });
      setResult(data);
      if (data.success && !dryRun) {
        toast({ title: t('channexAddHotel.toasts.onboarded'), status: 'success', duration: 5000 });
        onDone?.();
      }
    } catch (err) {
      toast({
        title: t('channexAddHotel.toasts.onboardingFailed'),
        description: err.response?.data?.detail || err.message,
        status: 'error', duration: 8000,
      });
    } finally {
      setBusy(false);
    }
  };

  return (
    <Modal isOpen={isOpen} onClose={onClose} size="xl">
      <ModalOverlay />
      <ModalContent>
        <ModalHeader>{t('channexAddHotel.title')}</ModalHeader>
        <ModalCloseButton />
        <ModalBody>
          <VStack align="stretch" spacing={4}>
            <FormControl>
              <FormLabel fontSize="sm">{t('channexAddHotel.form.hotel')}</FormLabel>
              <Select
                placeholder={loading ? t('channexAddHotel.form.loading') : t('channexAddHotel.form.selectHotel')}
                value={hotelId}
                onChange={(e) => { setHotelId(e.target.value); setResult(null); }}
                size="sm"
              >
                {hotels.map((h) => (
                  <option key={h.id} value={h.id}>
                    {h.channex_config_id
                      ? t('channexAddHotel.form.hotelOptionOnChannex', { name: h.name, code: h.code, currency: h.currency })
                      : t('channexAddHotel.form.hotelOption', { name: h.name, code: h.code, currency: h.currency })}
                  </option>
                ))}
              </Select>
            </FormControl>

            {selected?.channex_config_id && (
              <Alert status="info" borderRadius="md" fontSize="sm">
                <AlertIcon />
                {t('channexAddHotel.alerts.alreadyOnChannex', { configId: selected.channex_config_id })}
              </Alert>
            )}

            {selected?.other_channel_managers?.length > 0 && (
              <Alert status="warning" borderRadius="md" fontSize="sm">
                <AlertIcon />
                <Box>
                  <Trans i18nKey="channexAddHotel.alerts.otherChannelManagers"
                    values={{ managers: selected.other_channel_managers.join(', ') }}
                    components={{ b: <b /> }} />
                </Box>
              </Alert>
            )}

            <FormControl>
              <FormLabel fontSize="sm">{t('channexAddHotel.form.callbackUrl')}</FormLabel>
              <Input
                size="sm" placeholder="https://devapi.staydesko.com"
                value={callbackUrl} onChange={(e) => setCallbackUrl(e.target.value)}
              />
              <FormHelperText fontSize="xs">
                {t('channexAddHotel.form.callbackUrlHelp')}
              </FormHelperText>
            </FormControl>

            <Alert status="info" borderRadius="md" fontSize="xs">
              <AlertIcon />
              <Box>
                {t('channexAddHotel.alerts.noApiKey')}
              </Box>
            </Alert>

            {result && (
              <Box borderWidth="1px" borderRadius="md" p={3} bg="gray.50">
                <HStack mb={2}>
                  <Badge colorScheme={result.success ? 'green' : 'red'}>
                    {result.success ? t('channexAddHotel.result.success') : t('channexAddHotel.result.failed')}
                  </Badge>
                  {result.property_id && (
                    <Code fontSize="xs">{result.property_id}</Code>
                  )}
                </HStack>
                <List spacing={1} fontSize="xs">
                  {(result.steps || []).map((s, i) => (
                    <ListItem key={i}>• {s}</ListItem>
                  ))}
                </List>
                {result.catalog?.skipped?.length > 0 && (
                  <>
                    <Divider my={2} />
                    <Text fontSize="xs" color="orange.600">{t('channexAddHotel.result.skipped')}</Text>
                    <List fontSize="xs" color="orange.700">
                      {result.catalog.skipped.map((s, i) => <ListItem key={i}>• {s}</ListItem>)}
                    </List>
                  </>
                )}
                {result.errors?.length > 0 && (
                  <>
                    <Divider my={2} />
                    <List fontSize="xs" color="red.600">
                      {result.errors.map((e, i) => <ListItem key={i}>• {e}</ListItem>)}
                    </List>
                  </>
                )}
              </Box>
            )}
          </VStack>
        </ModalBody>
        <ModalFooter gap={2}>
          <Button size="sm" variant="ghost" onClick={onClose}>{t('channexAddHotel.buttons.close')}</Button>
          <Button size="sm" isDisabled={!hotelId} isLoading={busy}
                  onClick={() => run(true)}>
            {t('channexAddHotel.buttons.dryRun')}
          </Button>
          <Button size="sm" colorScheme="blue" isDisabled={!hotelId} isLoading={busy}
                  onClick={() => run(false)}>
            {t('channexAddHotel.buttons.onboard')}
          </Button>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
};

export default AddHotelModal;
